import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Search, Filter, DollarSign, AlertTriangle, TrendingUp, Calendar, Eye, Edit, Trash2, CheckSquare, Square } from "lucide-react";
import Layout from "../components/Layout";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {Calendar as CalendarComponent} from "../components/ui/calendar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getStatusBadgeClass = (status) => {
  const classes = {
    "Pendiente": "badge-por-comenzar",
    "Parcial": "badge-proximo",
    "Pagado": "badge-activo",
    "Vencido": "badge-vencido"
  };
  return `badge ${classes[status] || ""}`;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(amount);
};

const formatDate = (dateString) => {
  const [year, month, day] = dateString.split('T')[0].split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('es-MX');
};

export default function Cobranza({ user, onLogout }) {
  const [cargos, setCargos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  
  // Selection state
  const [selectedCargos, setSelectedCargos] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Date filter state
  const [filterType, setFilterType] = useState("mes_actual");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  
  // Get period label for stats cards
  const getPeriodoLabel = () => {
    if (filterType === "mes_actual") {
      const now = new Date();
      const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
    }
    if (filterType === "ultimos_30") return "Últ. 30 días";
    if (filterType === "ultimos_60") return "Últ. 60 días";
    if (filterType === "personalizado" && dateRange.from && dateRange.to) {
      return "Período";
    }
    return "Período";
  };
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState(null);
  const [paymentData, setPaymentData] = useState({
    fecha_pago: new Date().toISOString().split('T')[0],
    importe: "",
    metodo_pago: "Transferencia",
    referencia: "",
    nota: "",
    comprobante: null
  });
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailCargo, setDetailCargo] = useState(null);
  const [cargoPagos, setCargoPagos] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCargo, setEditCargo] = useState(null);
  const [editData, setEditData] = useState({
    descripcion: "",
    fecha_vencimiento: "",
    importe: ""
  });
  const [editLoading, setEditLoading] = useState(false);
  
  // Edit payment modal state
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [editingPago, setEditingPago] = useState(null);
  const [editPaymentData, setEditPaymentData] = useState({
    fecha_pago: "",
    importe: "",
    metodo_pago: "",
    referencia: "",
    nota: "",
    comprobante: null,
    eliminar_comprobante: false
  });
  const [editPaymentLoading, setEditPaymentLoading] = useState(false);
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  const navigate = useNavigate();
  const isAdmin = (user?.rol === "admin" || user?.rol === "maestro");

  useEffect(() => {
    fetchData();
  }, [statusFilter, filterType, dateRange]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // Calculate date range based on filter
      let startDate = "";
      let endDate = "";
      
      if (filterType === "mes_actual") {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        startDate = firstDay.toISOString().split('T')[0];
        endDate = lastDay.toISOString().split('T')[0];
      } else if (filterType === "ultimos_30") {
        const today = new Date();
        const past = new Date(today);
        past.setDate(past.getDate() - 30);
        startDate = past.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else if (filterType === "ultimos_60") {
        const today = new Date();
        const past = new Date(today);
        past.setDate(past.getDate() - 60);
        startDate = past.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else if (filterType === "personalizado" && dateRange.from && dateRange.to) {
        startDate = dateRange.from.toISOString().split('T')[0];
        endDate = dateRange.to.toISOString().split('T')[0];
      }

      const [cargosRes, statsRes] = await Promise.all([
        axios.get(`${API}/cobranza/cargos?estado=${statusFilter}&search=${searchTerm}&start_date=${startDate}&end_date=${endDate}`, { headers }),
        axios.get(`${API}/cobranza/dashboard?start_date=${startDate}&end_date=${endDate}`, { headers })
      ]);

      setCargos(cargosRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchData();
  };

  const handleSelectCargo = (cargoId) => {
    setSelectedCargos(prev => {
      if (prev.includes(cargoId)) {
        return prev.filter(id => id !== cargoId);
      } else {
        return [...prev, cargoId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCargos([]);
    } else {
      setSelectedCargos(cargos.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  const openPaymentModal = (cargo) => {
    setSelectedCargo(cargo);
    setPaymentData({
      fecha_pago: new Date().toISOString().split('T')[0],
      importe: cargo.saldo_pendiente.toString(),
      metodo_pago: "Transferencia",
      referencia: "",
      nota: "",
      comprobante: null
    });
    setShowPaymentModal(true);
  };

  const handleRegisterPayment = async () => {
    setPaymentLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('cargo_id', selectedCargo.id);
      formData.append('fecha_pago', paymentData.fecha_pago);
      formData.append('importe', parseFloat(paymentData.importe));
      formData.append('metodo_pago', paymentData.metodo_pago);
      
      if (paymentData.referencia) {
        formData.append('referencia', paymentData.referencia);
      }
      if (paymentData.nota) {
        formData.append('nota', paymentData.nota);
      }
      if (paymentData.comprobante) {
        formData.append('comprobante', paymentData.comprobante);
      }
      
      await axios.post(
        `${API}/cobranza/pagos`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      toast.success("Pago registrado exitosamente");
      setShowPaymentModal(false);
      fetchData();
      setSelectedCargos([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar el pago");
    } finally {
      setPaymentLoading(false);
    }
  };

  const openEditPaymentModal = (pago) => {
    setEditingPago(pago);
    setEditPaymentData({
      fecha_pago: pago.fecha_pago,
      importe: pago.importe.toString(),
      metodo_pago: pago.metodo_pago,
      referencia: pago.referencia || "",
      nota: pago.nota || "",
      comprobante: null,
      eliminar_comprobante: false
    });
    setShowEditPaymentModal(true);
  };

  const handleUpdatePayment = async () => {
    setEditPaymentLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('fecha_pago', editPaymentData.fecha_pago);
      formData.append('importe', parseFloat(editPaymentData.importe));
      formData.append('metodo_pago', editPaymentData.metodo_pago);
      formData.append('referencia', editPaymentData.referencia || '');
      formData.append('nota', editPaymentData.nota || '');
      formData.append('eliminar_comprobante', editPaymentData.eliminar_comprobante);
      
      if (editPaymentData.comprobante) {
        formData.append('comprobante', editPaymentData.comprobante);
      }
      
      await axios.put(
        `${API}/cobranza/pagos/${editingPago.id}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      toast.success("Pago actualizado exitosamente");
      setShowEditPaymentModal(false);
      setEditingPago(null);
      
      // Refresh detail modal if open
      if (showDetailModal && detailCargo) {
        fetchCargoPagos(detailCargo.id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar el pago");
    } finally {
      setEditPaymentLoading(false);
    }
  };

  const openDetailModal = async (cargo) => {
    setDetailCargo(cargo);
    setShowDetailModal(true);
    setDetailLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cobranza/pagos/cargo/${cargo.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCargoPagos(response.data);
    } catch (error) {
      toast.error("Error al cargar los pagos");
    } finally {
      setDetailLoading(false);
    }
  };

  const openEditModal = (cargo) => {
    setEditCargo(cargo);
    setEditData({
      descripcion: cargo.descripcion,
      fecha_vencimiento: cargo.fecha_vencimiento.split('T')[0],
      importe: cargo.importe.toString()
    });
    setShowEditModal(true);
  };

  const handleEditCargo = async () => {
    setEditLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/cobranza/cargos/${editCargo.id}`,
        {
          descripcion: editData.descripcion,
          fecha_vencimiento: editData.fecha_vencimiento,
          importe: parseFloat(editData.importe)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Cargo actualizado exitosamente");
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar el cargo");
    } finally {
      setEditLoading(false);
    }
  };

  const handleBulkPayment = () => {
    if (selectedCargos.length === 0) {
      toast.error("No hay cargos seleccionados");
      return;
    }
    setConfirmAction("payment");
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedCargos.length === 0) {
      toast.error("No hay cargos seleccionados");
      return;
    }
    setConfirmAction("delete");
    setShowConfirmModal(true);
  };

  const executeBulkAction = async () => {
    setConfirmLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      if (confirmAction === "payment") {
        const response = await axios.post(
          `${API}/cobranza/cargos/bulk-payment`,
          { cargo_ids: selectedCargos },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(response.data.message);
      } else if (confirmAction === "delete") {
        const response = await axios.delete(
          `${API}/cobranza/cargos/bulk-delete`,
          { 
            data: { cargo_ids: selectedCargos },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success(response.data.message);
      }
      
      setShowConfirmModal(false);
      setSelectedCargos([]);
      setSelectAll(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al ejecutar la acción");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedCargos = [...cargos].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Handle different types
      if (key === 'importe' || key === 'saldo_pendiente') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (key === 'fecha_vencimiento') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setCargos(sortedCargos);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400 ml-1">⇅</span>;
    }
    return sortConfig.direction === 'asc' ? 
      <span className="text-blue-600 ml-1">↑</span> : 
      <span className="text-blue-600 ml-1">↓</span>;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Cobranza</h1>
            <p className="text-gray-500 mt-1">Gestiona los pagos y cobros de los contratos</p>
          </div>
        </div>

        {/* Stats */}
        {!loading && stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card data-testid="stat-cartera-total">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Cartera Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-blue-600">{formatCurrency(stats.cartera_total)}</div>
                  <DollarSign className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-cartera-vencida">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Cartera Vencida</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-red-600">{formatCurrency(stats.cartera_vencida)}</div>
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-rentas-mes">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Rentas {getPeriodoLabel()}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-purple-600">{formatCurrency(stats.rentas_mes_actual)}</div>
                  <Calendar className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-pagos-mes">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Pagos {getPeriodoLabel()}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(stats.pagos_mes_actual)}</div>
                  <TrendingUp className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters and Actions Bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              {/* Search and Filters */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      placeholder="Buscar por contrato, propietario, inquilino..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los estados</SelectItem>
                    <SelectItem value="Pendiente">Pendiente</SelectItem>
                    <SelectItem value="Parcial">Parcial</SelectItem>
                    <SelectItem value="Pagado">Pagado</SelectItem>
                    <SelectItem value="Vencido">Vencido</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue placeholder="Período" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mes_actual">Este mes</SelectItem>
                    <SelectItem value="ultimos_30">Últimos 30 días</SelectItem>
                    <SelectItem value="ultimos_60">Últimos 60 días</SelectItem>
                    <SelectItem value="personalizado">Personalizado</SelectItem>
                  </SelectContent>
                </Select>

                {filterType === "personalizado" && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline">
                        <Calendar className="w-4 h-4 mr-2" />
                        Seleccionar fechas
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <CalendarComponent
                        mode="range"
                        selected={dateRange}
                        onSelect={setDateRange}
                        numberOfMonths={2}
                      />
                    </PopoverContent>
                  </Popover>
                )}
                
                <Button onClick={handleSearch}>
                  <Search className="w-4 h-4 mr-2" />
                  Buscar
                </Button>
              </div>

              {/* Bulk Actions Bar (only for admin) */}
              {isAdmin && selectedCargos.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedCargos.length} cargo(s) seleccionado(s)
                  </span>
                  <div className="flex gap-2 ml-auto">
                    <Button
                      size="sm"
                      onClick={handleBulkPayment}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Registrar Pagos
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleBulkDelete}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Cargos Table */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Cargando...</div>
            ) : cargos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No hay cargos para mostrar</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {isAdmin && (
                        <th className="px-6 py-3 text-left">
                          <button onClick={handleSelectAll} className="flex items-center">
                            {selectAll ? (
                              <CheckSquare className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        </th>
                      )}
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('inmueble_nombre')}
                      >
                        <div className="flex items-center">
                          Inmueble
                          <SortIcon columnKey="inmueble_nombre" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('tipo')}
                      >
                        <div className="flex items-center">
                          Tipo
                          <SortIcon columnKey="tipo" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('descripcion')}
                      >
                        <div className="flex items-center">
                          Descripción
                          <SortIcon columnKey="descripcion" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('fecha_vencimiento')}
                      >
                        <div className="flex items-center">
                          Vencimiento
                          <SortIcon columnKey="fecha_vencimiento" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('importe')}
                      >
                        <div className="flex items-center">
                          Importe
                          <SortIcon columnKey="importe" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('importe_pagado')}
                      >
                        <div className="flex items-center">
                          Pagado
                          <SortIcon columnKey="importe_pagado" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('saldo_pendiente')}
                      >
                        <div className="flex items-center">
                          Saldo
                          <SortIcon columnKey="saldo_pendiente" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Días venc.
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('estado')}
                      >
                        <div className="flex items-center">
                          Estado
                          <SortIcon columnKey="estado" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cargos.map((cargo) => (
                      <tr key={cargo.id} className="hover:bg-gray-50">
                        {isAdmin && (
                          <td className="px-6 py-4">
                            <button onClick={() => handleSelectCargo(cargo.id)}>
                              {selectedCargos.includes(cargo.id) ? (
                                <CheckSquare className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-400" />
                              )}
                            </button>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {cargo.property_id ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/propiedades/${cargo.property_id}`); }}
                              className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                              data-testid={`link-propiedad-${cargo.id}`}
                            >
                              {cargo.inmueble_nombre}
                            </button>
                          ) : (
                            cargo.inmueble_nombre
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {cargo.tipo}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {cargo.descripcion}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDate(cargo.fecha_vencimiento)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {formatCurrency(cargo.importe)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {cargo.importe_pagado > 0 ? (
                            <span className={cargo.saldo_pendiente > 0.01 ? "text-amber-600 font-medium" : "text-emerald-600"} data-testid={`pagado-${cargo.id}`}>
                              {formatCurrency(cargo.importe_pagado)}
                              {cargo.saldo_pendiente > 0.01 && <span className="block text-[10px] text-amber-500">Parcial</span>}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <span className={cargo.saldo_pendiente > 0.01 ? "text-red-600" : "text-emerald-600"} data-testid={`saldo-${cargo.id}`}>
                            {formatCurrency(cargo.saldo_pendiente)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {(() => {
                            const dias = Math.floor((new Date() - new Date(cargo.fecha_vencimiento + "T00:00:00")) / 86400000);
                            if (cargo.saldo_pendiente <= 0.01 || dias <= 0) return <span className="text-gray-400">—</span>;
                            return (
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${dias > 30 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`dias-vencidos-${cargo.id}`}>
                                {dias}d
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={getStatusBadgeClass(cargo.estado)}>
                            {cargo.estado}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDetailModal(cargo)}
                              title="Ver detalle"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {isAdmin && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEditModal(cargo)}
                                  title="Editar"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {cargo.estado !== "Pagado" && (
                                  <Button
                                    size="sm"
                                    onClick={() => openPaymentModal(cargo)}
                                    className="bg-green-600 hover:bg-green-700"
                                    title="Registrar pago"
                                  >
                                    $
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Pago</DialogTitle>
            <DialogDescription>
              {selectedCargo && `${selectedCargo.numero_contrato} - ${selectedCargo.descripcion}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Fecha de Pago</Label>
              <Input
                type="date"
                value={paymentData.fecha_pago}
                onChange={(e) => setPaymentData({...paymentData, fecha_pago: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Importe</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentData.importe}
                onChange={(e) => setPaymentData({...paymentData, importe: e.target.value})}
              />
              <p className="text-sm text-gray-500">
                Saldo pendiente: {selectedCargo && formatCurrency(selectedCargo.saldo_pendiente)}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Método de Pago</Label>
              <Select 
                value={paymentData.metodo_pago} 
                onValueChange={(value) => setPaymentData({...paymentData, metodo_pago: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Transferencia">Transferencia</SelectItem>
                  <SelectItem value="Efectivo">Efectivo</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Referencia</Label>
              <Input
                value={paymentData.referencia}
                onChange={(e) => setPaymentData({...paymentData, referencia: e.target.value})}
                placeholder="Número de referencia o folio"
              />
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={paymentData.nota}
                onChange={(e) => setPaymentData({...paymentData, nota: e.target.value})}
                placeholder="Notas adicionales"
                rows={3}
              />
            </div>

            {(paymentData.metodo_pago === "Transferencia" || paymentData.metodo_pago === "Cheque") && (
              <div className="space-y-2">
                <Label>Comprobante de Pago {paymentData.metodo_pago === "Transferencia" ? "(Opcional)" : "(Opcional)"}</Label>
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setPaymentData({...paymentData, comprobante: e.target.files[0]})}
                />
                <p className="text-xs text-gray-500">
                  Sube el comprobante de {paymentData.metodo_pago.toLowerCase()}. Formatos permitidos: PDF, JPG, PNG
                </p>
                {paymentData.comprobante && (
                  <p className="text-sm text-green-600">
                    ✓ {paymentData.comprobante.name}
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleRegisterPayment} disabled={paymentLoading}>
              {paymentLoading ? "Registrando..." : "Registrar Pago"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Detalle del Cargo</DialogTitle>
          </DialogHeader>
          
          {detailCargo && (
            <div className="space-y-6">
              {/* Cargo Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Inmueble</p>
                  <p className="font-medium">{detailCargo.inmueble_nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Tipo</p>
                  <p className="font-medium">{detailCargo.tipo}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Descripción</p>
                  <p className="font-medium">{detailCargo.descripcion}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Vencimiento</p>
                  <p className="font-medium">{formatDate(detailCargo.fecha_vencimiento)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Importe</p>
                  <p className="font-medium text-lg">{formatCurrency(detailCargo.importe)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Saldo Pendiente</p>
                  <p className="font-medium text-lg text-red-600">{formatCurrency(detailCargo.saldo_pendiente)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Propietario</p>
                  <p className="font-medium">{detailCargo.propietario_nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Inquilino</p>
                  <p className="font-medium">{detailCargo.inquilino_nombre}</p>
                </div>
              </div>

              {/* Payments History */}
              <div>
                <h3 className="text-lg font-medium mb-3">Historial de Pagos</h3>
                {detailLoading ? (
                  <p className="text-gray-500 text-center py-4">Cargando pagos...</p>
                ) : cargoPagos.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No hay pagos registrados</p>
                ) : (
                  <div className="space-y-2">
                    {cargoPagos.map((pago) => (
                      <div key={pago.id} className="p-3 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium">{formatCurrency(pago.importe)}</p>
                            <p className="text-sm text-gray-500">
                              {formatDate(pago.fecha_pago)} • {pago.metodo_pago}
                            </p>
                            {pago.referencia && (
                              <p className="text-xs text-gray-400">Ref: {pago.referencia}</p>
                            )}
                            {pago.nota && (
                              <p className="text-sm text-gray-500 mt-1">{pago.nota}</p>
                            )}
                            {pago.comprobante && (
                              <p className="text-xs text-green-600 mt-1">📎 {pago.comprobante.nombre_archivo}</p>
                            )}
                          </div>
                          <div className="flex gap-2 ml-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditPaymentModal(pago)}
                              title="Editar pago"
                            >
                              ✏️
                            </Button>
                            {pago.comprobante && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const token = localStorage.getItem("token");
                                  window.open(`${API}/cobranza/pagos/${pago.id}/comprobante?token=${token}`, '_blank');
                                }}
                                title="Ver comprobante"
                              >
                                📄
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowDetailModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Cargo</DialogTitle>
            <DialogDescription>
              {editCargo && `${editCargo.numero_contrato} - ${editCargo.tipo}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input
                value={editData.descripcion}
                onChange={(e) => setEditData({...editData, descripcion: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Fecha de Vencimiento</Label>
              <Input
                type="date"
                value={editData.fecha_vencimiento}
                onChange={(e) => setEditData({...editData, fecha_vencimiento: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Importe</Label>
              <Input
                type="number"
                step="0.01"
                value={editData.importe}
                onChange={(e) => setEditData({...editData, importe: e.target.value})}
              />
              <p className="text-sm text-gray-500">
                El saldo pendiente se ajustará automáticamente
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEditCargo} disabled={editLoading}>
              {editLoading ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Acción</DialogTitle>
            <DialogDescription>
              {confirmAction === "payment" 
                ? `¿Deseas registrar el pago completo de ${selectedCargos.length} cargo(s) seleccionado(s)? La fecha de pago será la fecha de vencimiento de cada cargo.`
                : `¿Estás seguro de eliminar ${selectedCargos.length} cargo(s) seleccionado(s)? Esta acción no se puede deshacer.`
              }
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={executeBulkAction} 
              disabled={confirmLoading}
              className={confirmAction === "delete" ? "bg-red-600 hover:bg-red-700" : ""}
            >
              {confirmLoading ? "Procesando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Payment Modal */}
      <Dialog open={showEditPaymentModal} onOpenChange={setShowEditPaymentModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Pago</DialogTitle>
            <DialogDescription>
              Modifica los datos del pago y gestiona el comprobante
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha de Pago *</Label>
                <Input
                  type="date"
                  value={editPaymentData.fecha_pago}
                  onChange={(e) => setEditPaymentData({...editPaymentData, fecha_pago: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Importe *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editPaymentData.importe}
                  onChange={(e) => setEditPaymentData({...editPaymentData, importe: e.target.value})}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Método de Pago *</Label>
              <Select 
                value={editPaymentData.metodo_pago}
                onValueChange={(value) => setEditPaymentData({...editPaymentData, metodo_pago: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Efectivo">Efectivo</SelectItem>
                  <SelectItem value="Transferencia">Transferencia</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Referencia</Label>
              <Input
                value={editPaymentData.referencia}
                onChange={(e) => setEditPaymentData({...editPaymentData, referencia: e.target.value})}
                placeholder="Número de referencia o folio"
              />
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={editPaymentData.nota}
                onChange={(e) => setEditPaymentData({...editPaymentData, nota: e.target.value})}
                placeholder="Notas adicionales"
                rows={3}
              />
            </div>

            {/* Comprobante Management */}
            <div className="space-y-3 p-4 border rounded-lg bg-gray-50">
              <Label className="text-base font-semibold">Gestión de Comprobante</Label>
              
              {editingPago?.comprobante && !editPaymentData.eliminar_comprobante && (
                <div className="p-3 bg-white rounded border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-700">📎 Comprobante actual:</p>
                      <p className="text-sm text-gray-600">{editingPago.comprobante.nombre_archivo}</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditPaymentData({...editPaymentData, eliminar_comprobante: true})}
                      className="text-red-600 hover:text-red-700"
                    >
                      🗑️ Eliminar
                    </Button>
                  </div>
                </div>
              )}

              {editPaymentData.eliminar_comprobante && (
                <div className="p-3 bg-red-50 rounded border border-red-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-red-700">✓ El comprobante será eliminado</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditPaymentData({...editPaymentData, eliminar_comprobante: false})}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>
                  {editPaymentData.eliminar_comprobante ? 'Subir nuevo comprobante (Opcional)' : 'Reemplazar comprobante (Opcional)'}
                </Label>
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setEditPaymentData({...editPaymentData, comprobante: e.target.files[0]})}
                />
                <p className="text-xs text-gray-500">
                  Formatos permitidos: PDF, JPG, PNG
                </p>
                {editPaymentData.comprobante && (
                  <p className="text-sm text-green-600">
                    ✓ Nuevo archivo: {editPaymentData.comprobante.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditPaymentModal(false);
                setEditingPago(null);
              }}
              disabled={editPaymentLoading}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleUpdatePayment}
              disabled={editPaymentLoading}
            >
              {editPaymentLoading ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
